import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from 'srcReact/components/common2/Modal'
import styles from './styles.module.scss'
import { profileActions } from 'srcReact/MyProfile/action'
import Avatar from 'srcReact/components/common2/Avatar'
import { useDispatch, useSelector } from 'react-redux'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import ImageCropper from 'srcReact/components/common2/ImageCropper'
import Spinner from 'srcReact/components/common2/Spinner'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import { commonActions } from 'srcReact/components'
import useInterval from 'srcReact/helpers/hooks/useInterval'
import ConfirmModal from 'srcReact/components/common2/ConfirmModal'
import { loginActions } from 'srcReact/LoginPage'
import IconButton from 'srcReact/components/common2/IconButton'
import { redirectToPage } from 'srcReact/helpers/history'
import { organizationActions } from 'srcReact/OrganizationPage'

function UpdatePictureModal({ isOpen, type = '', decryptedCoverImage, closeModal, ctaPopup = '' }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [newlyUploadedFile, setNewlyUploadedFile] = useState(null)
  const [pictureUploadStarted, setPictureUploadStarted] = useState(false)
  const [imagePath, setImagePath] = useState('')
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false)
  const [removePictureLoaderText, setRemovePictureLoaderText] = useState('')
  const [showLoader, setShowLoader] = useState(false)
  const [iOSPictureSizeError, setIOSPictureSizeError] = useState('')

  const inputPicture = useRef(null)

  const {
    login: { user: loggedInUser },
    common: { isMobile, isTablet, isNative, isDesktop, fileCreation },
    myProfile: {
      profileData,
      putProfilePopupSuccess,
      putProfilePopupFailure,
      imageCropper,
      pictureUpdating,
      pictureUpdated,
      putProfilePicture
    },
    organization: {
      current_organization,
      header: { loading: headerInfoLoading }
    }
  } = useSelector((state) => state)

  useEffect(() => {
    if (fileCreation && fileCreation.files?.length > 0 && fileCreation.files[0]?.url) {
      setImagePath(fileCreation.files[0].url)
    } else {
      setImagePath('')
    }
  }, [fileCreation && fileCreation.files?.length > 0 && fileCreation.files[0].url])

  useEffect(() => {
    //update login profile picture once updated on this page
    if (!pictureUpdating && pictureUpdated) {
      if (pictureUploadStarted) {
        setPictureUploadStarted(false)
        if (type === 'avatar') {
          dispatch(
            loginActions.setPictureDetails(
              putProfilePicture.photo_url,
              putProfilePicture.original_tag,
              putProfilePicture.is_original,
              putProfilePicture.mime_type
            )
          )
        }
        callHeaderInfoForNative() //to update myprofile ctas
      }
      if (!headerInfoLoading) {
        setTimeout(() => {
          localStorage.removeItem('profile_picture_updating')
          dispatch(profileActions.clearProfilePictureUpdateData())
        }, 15000)
      }
    }
  }, [pictureUploadStarted, pictureUpdating, pictureUpdated, headerInfoLoading])

  useEffect(() => {
    if (pictureUploadStarted || pictureUpdating || fileCreation.loadingFile) {
      setShowLoader(true)
    } else {
      setShowLoader(false)
      setRemovePictureLoaderText('')
    }
  }, [pictureUploadStarted, pictureUpdating, fileCreation.loadingFile])

  const callHeaderInfoForNative = () => {
    if (isNative) {
      const organizationId = getCurrentOrgId()
      dispatch(organizationActions.getHeaderInfo(organizationId))
    }
  }
  const handleCloseModal = () => {
    setShowLoader(false)
    setRemovePictureLoaderText('')
    closeModal()
  }

  useEffect(() => {
    if (fileCreation && fileCreation.files?.length > 0 && fileCreation.files[0].id > 0) {
      const { id } = fileCreation.files[0]
      const orgId = getCurrentOrgId()
      const payload = {
        picture_id: id,
        organization_id: orgId
      }
      if (type === 'avatar') {
        dispatch(profileActions.putProfilePicture(id, orgId, ctaPopup))
      } else if (type === 'cover') {
        dispatch(profileActions.saveCoverPicture(id, orgId))
      }
      dispatch(commonActions.fileCreationRemove())
      dispatch(profileActions.setCropper(false))
    }
  }, [fileCreation?.files?.length > 0 && fileCreation?.files[0]?.url])

  useInterval(() => {
    const processedCroppedImage = JSON.parse(localStorage.getItem('files_creation'))
    if (processedCroppedImage) {
      setPictureUploadStarted(true)
      if (type === 'avatar') {
        localStorage.setItem('profile_picture_updating', true)
      }
      dispatch(
        commonActions.fileCreation([processedCroppedImage[0]], {
          type: 'picture',
          is_logged: 1
        })
      )
      localStorage.removeItem('files_creation')
    }
  }, appConstantSettings.NATIVE_PICTURE_UPDATE_INTERVAL)

  const changePicture = (e) => {
    if (e) {
      e.stopPropagation()
    }
    if (isNative) {
      setNewlyUploadedFile(null)
      if (type === 'cover') {
        redirectToPage(`/cover/picture/upload`, isNative);
        return;
      }
      redirectToPage(`/profile/picture/upload`, isNative);
      return;
    }
    inputPicture.current.click()
  }

  const selectFile = async (e) => {
    e.stopPropagation()
    const file = e.target.files[0]
    setNewlyUploadedFile(file)
    dispatch(profileActions.setCropper(true))
  }

  const openDeleteConfirmationModal = (e) => {
    if (e) {
      e.stopPropagation()
    }
    setIsDeleteConfirmModalOpen(true)
  }
  const closeDeleteConfirmationModal = () => {
    setIsDeleteConfirmModalOpen(false)
  }
  const handlePicSkip = () => {
    dispatch(profileActions.setCropper(false))
  }
  const handleCancelUpdatePicture = () => {
    dispatch(profileActions.setCropper(false))
    setNewlyUploadedFile(null)
  }

  const handleRemovePicture = () => {
    setPictureUploadStarted(true)
    setImagePath('')
    const orgId = getCurrentOrgId()
    if (type === 'avatar') {
      setRemovePictureLoaderText(t('my_profile.popup.removePictureLoading'))
      localStorage.setItem('profile_picture_updating', true)
      dispatch(profileActions.putProfilePicture(0, orgId, ctaPopup))
    } else if (type === 'cover') {
      setRemovePictureLoaderText(t('my_profile.popup.removeCoverPictureLoading'))
      dispatch(profileActions.saveCoverPicture(0, orgId))
    }
    closeDeleteConfirmationModal()
  }

  return (
    <Modal
      isOpen={isOpen}
      {...(type === 'cover' && isDesktop
        ? { customContentStyle: { maxWidth: '56.25rem', width: 'fit-content', minWidth: '32.5rem' } }
        : {})}
      closeOnDocumentClick={isDeleteConfirmModalOpen ? false : true}
      handleClose={handleCloseModal}
      title={
        type === 'avatar'
          ? t('my_profile.info_block_edit.picture.title')
          : t('my_profile.info_block_edit.picture.coverTitle')
      }
      subTitle={type === 'avatar' ? t('my_profile.info_block_edit.picture.subtitle') : ''}>
      <div className={styles.uploadContainer}>
        {imageCropper && newlyUploadedFile ? (
          <ImageCropper
            editMode={true}
            handleSkip={handlePicSkip}
            handleCancel={handleCancelUpdatePicture}
            setShowSaveCancelButtons={true}
            callbackOnImageUploadStart={() => {
              setPictureUploadStarted(true)
              if (type === 'avatar') {
                localStorage.setItem('profile_picture_updating', true)
              }
            }}
            // cropSize={{
            //   width: appConstantSettings.PROFILE_PIC_MIN_SIZE,
            //   height: appConstantSettings.PROFILE_PIC_MIN_SIZE
            // }}
            aspectRatio={1}
            // aspectRatio={newlyUploadedFile.type === 'avatar' ? 1 / 1 : isMobile || isTablet ? 4.0 : 5.9} //mobile-603/150  desktop- 875/150
            skipText={t('global.cancel')}
            showUploadZone={true}
            showNextButton={false}
            externallyUploadedFile={newlyUploadedFile}
            hasRoundShape={type === 'avatar'}
            containerCSS={`${styles.imageCropperContainer} ${
              type === 'avatar' ? styles.imageCropperSquareContainer : styles.imageCropperRectangleContainer
            }`}
            actionButtonCSS={styles.cropperActionButton}
            actionWrapperCSS={styles.cropperActionWrapper}
            cropContainerCSS={styles.cropContainer}
          />
        ) : (
          <button className={`${styles.pictureWrap}`} onClick={changePicture}>
            {type === 'avatar' ? (
              <Avatar
                imgWrapperSize='large120'
                userId={profileData.user.id}
                profilePicture={imagePath || loggedInUser.profile_picture}
                firstname={profileData.user.firstName}
                lastname={profileData.user.lastName}
                onClickAction={changePicture}
                deleteMode={loggedInUser.original_tag}
                deleteAction={openDeleteConfirmationModal}
                mimeType={loggedInUser.mime_type}
                originalTag={loggedInUser.original_tag}
                isOriginal={loggedInUser.is_original}
              />
            ) : (
              <div className={styles.coverImageWithRemove}>
                {decryptedCoverImage ? (
                  <img alt='' src={imagePath || decryptedCoverImage} className={styles.coverImage} />
                ) : (
                  <div className={styles.emptyCoverImage} />
                )}
                <IconButton
                  iconName={decryptedCoverImage || imagePath ? 'close24' : 'add'}
                  iconSize='large24'
                  className={styles.removeCoverButton}
                  iconCSS={styles.removeCoverIcon}
                  onClick={(e) => {
                    decryptedCoverImage || imagePath ? openDeleteConfirmationModal(e) : changePicture(e)
                  }}
                />
              </div>
            )}
            <div className={styles.changeLabel}>
              {type === 'avatar'
                ? t('my_profile.info_block_edit.picture.cta')
                : t('my_profile.info_block_edit.picture.ctaCover')}
              <input
                id='fileInput'
                ref={inputPicture}
                style={{ display: 'none' }}
                type='file'
                accept={appConstantSettings.CROPPER_ACCEPTED_IMAGE_TYPES}
                onChange={(e) => selectFile(e, 'avatar')}
              />
            </div>
          </button>
        )}
        {showLoader && (
          <div className={styles.pictureLoading}>
            {removePictureLoaderText ||
              t(type === 'avatar' ? 'my_profile.popup.pictureLoading' : 'my_profile.popup.coverPictureLoading')}
            <Spinner />
          </div>
        )}
        {iOSPictureSizeError ? <span className={styles.iOSPictureSizeError}>{iOSPictureSizeError}</span> : ''}
      </div>
      <ConfirmModal
        isOpen={isDeleteConfirmModalOpen}
        closeModal={closeDeleteConfirmationModal}
        nested={true}
        closeOnDocumentClick={true}
        title={type === 'avatar' ? t('my_profile.popup.picDeleteConfirm') : t('my_profile.popup.coverPicDeleteConfirm')}
        handlePrimaryButton={handleRemovePicture}
        handleSecondaryButton={closeDeleteConfirmationModal}
      />
    </Modal>
  )
}

export default UpdatePictureModal
